//Libs
import { Fragment, useEffect, useState } from "react";
import { useMutation } from "react-query";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { Column, Workbook } from "exceljs";
import { saveAs } from "file-saver";
import { ICellRendererParams } from "ag-grid-community";
import { RangeValue } from "@react-types/shared";
import {
  CalendarDate,
  CalendarDateTime,
  getLocalTimeZone,
  toCalendarDate,
  today,
  ZonedDateTime,
} from "@internationalized/date";
import AddCircleIcon from "@mui/icons-material/AddCircle";

//Local
import TaskListPage from "./TaskListPage";
import * as strings from "../../strings";
import { DbRead, DbWrite } from "../../database";
import { useSiteKeyCompaniesStore } from "../../store/site-key-companies";
import { useSiteKeyLocationsStore } from "../../store/site-key-locations";
import LoadingClipboardAnimation from "../../components/LoadingClipBoardAnimation";
import { ExistingTask } from "../../models/task";
import ConfirmRestoreTaskDialog from "../../components/ConfirmRestoreTaskDialog";
import { logger, logger as devLogger } from "../../logging";
import { useToastMessageStore } from "../../store/toast-messages";
import { createToastMessageID } from "../../utils";
import { useUserDisplayNamesStore } from "../../store/user-display-names-map";
import TaskTableColumnsDialog from "../../components/TaskTableColumnsDialog";
import { getTaskListTableCustomizedColumns } from "../../assets/js/getTaskListTableCustomizedColumns";
import { useSiteKeyDocStore } from "../../store/site-key-doc";
import { StyledTooltip } from "../../components/StyledTooltip";
import { formatTaskDataForExcelExport } from "../../assets/js/formatTaskDataForExcelExport";
import { useAuthStore } from "../../store/firebase-auth";
import DateRangePicker from "../../components/DateRangePicker";
import BaseButtonPrimary from "../../components/BaseButtonPrimary";
import { useStiltNav } from "../../navigation";

interface Props {
  siteKey: string;
}

export default function DeletedTaskListContainer({ siteKey }: Props) {
  const [getCompanyName, companyStoreIsLoading] = useSiteKeyCompaniesStore(
    (state) => [state.getCompanyName, state.loading],
  );
  const [getLocationTitle, locationStoreIsLoading, siteKeyLocationList] =
    useSiteKeyLocationsStore((state) => [
      state.getLocationTitle,
      state.loading,
      state.siteKeyLocationList,
    ]);
  const [userDisplayNamesMap, userDisplayNamesMapIsLoading] =
    useUserDisplayNamesStore((state) => [
      state.userDisplayNames,
      state.loading,
    ]);
  const [siteKeyDoc, siteKeyDocIsLoading] = useSiteKeyDocStore((state) => [
    state.siteKeyDoc,
    state.loading,
  ]);

  const firebaseUser = useAuthStore((state) => state.firebaseUser);
  const stiltNav = useStiltNav();

  const addMessage = useToastMessageStore((state) => state.addToastMessage);

  const [confirmRestoreTaskDialogOpen, setConfirmRestoreTaskDialogOpen] =
    useState<boolean>(false);
  const [isRestoringTaskLoading, setIsRestoringTaskLoading] =
    useState<boolean>(false);
  const [taskToBeRestored, setTaskToBeRestored] = useState<ExistingTask | null>(
    null,
  );

  const [range, setRange] = useState<RangeValue<CalendarDate>>({
    start: today(getLocalTimeZone()).subtract({
      days: siteKeyDoc?.customizations?.defaultInvoicePageDateRangeDays ?? 30,
    }),
    end: today(getLocalTimeZone()),
  });

  const siteKeyTaskSpecificDetails: Record<string, any> =
    siteKeyDoc?.customizations.taskSpecificDetails;

  function getAssignedToString(
    userDisplayNamesMap: Record<string, string>,
    assignedTo: string[] | undefined,
  ) {
    if (!assignedTo) return "";
    const displayNames: string[] = [];
    assignedTo.forEach((a) => {
      const displayName = userDisplayNamesMap[a];
      displayNames.push(displayName);
    });
    return displayNames.join(", ");
  }

  const renderMultipleUID = (params: ICellRendererParams): JSX.Element => {
    return <div>{getAssignedToString(userDisplayNamesMap, params.value)}</div>;
  };

  const {
    additionalColumnsSelection,
    customColumns,
    taskTableColumnsDefaultValues,
  } = getTaskListTableCustomizedColumns(
    siteKeyTaskSpecificDetails,
    renderMultipleUID,
    userDisplayNamesMap,
  );

  const localStorageColumnSelection = localStorage.getItem(
    "deletedTaskTableColumnsSelection",
  );

  const [deletedTasks, setDeletedTasks] = useState<ExistingTask[]>([]);

  const [deletedTaskListIsLoading, setDeletedTaskListIsLoading] =
    useState<boolean>(false);
  const [taskTableColumnsDialogOpen, setTaskTableColumnsDialogOpen] =
    useState<boolean>(false);
  const [taskTableColumnsSelection, setTaskTableColumnsSelection] = useState<
    Record<string, boolean>
  >(
    localStorageColumnSelection
      ? JSON.parse(localStorageColumnSelection)
      : { ...taskTableColumnsDefaultValues, action: true },
  );

  useEffect(() => {
    localStorage.setItem(
      "deletedTaskTableColumnsSelection",
      JSON.stringify(taskTableColumnsSelection),
    );
  }, [taskTableColumnsSelection]);

  /* QUERIES */

  useEffect(() => {
    async function getTasks() {
      if (!siteKey) return;
      if (!siteKeyDoc) return;

      setDeletedTaskListIsLoading(true);
      const deletedTasks = await DbRead.tasks.getAllDeleted(
        siteKey,
        range.start.toDate(siteKeyDoc.timezone),
        new Date(range.end.toDate(siteKeyDoc.timezone).setHours(23, 59, 59)),
      );

      setDeletedTasks(deletedTasks);
      setDeletedTaskListIsLoading(false);
    }

    getTasks();
  }, [siteKey, range, siteKeyDoc]);

  /* MUTATIONS */
  const mutateRestoreTask = useMutation(
    async (args: { taskRefPath: ExistingTask["refPath"] }) =>
      await DbWrite.tasks.restore(args.taskRefPath),
    {},
  );

  /* FUNCTIONS */
  async function handleConfirmRestoreTask() {
    if (taskToBeRestored == null) return;
    setIsRestoringTaskLoading(true);

    try {
      const { refPath, craftRecordID } = taskToBeRestored;
      const parentRecord =
        await DbRead.parentRecords.getByRefPath(craftRecordID);
      if (!parentRecord?.timestampRecordCreated) {
        addMessage({
          id: createToastMessageID(),
          message: strings.ERR_TASK_CANNOT_BE_RESTORED,
          dialog: false,
          type: "error",
        });
        return;
      }
      await mutateRestoreTask.mutateAsync({ taskRefPath: refPath });
      addMessage({
        id: createToastMessageID(),
        message: strings.successfulRestore(`${taskToBeRestored.title}`),
        dialog: false,
        type: "success",
      });
    } catch (error) {
      devLogger.error(`An error occurred during saveAssignedVehicleIDs`, error);
      addMessage({
        id: createToastMessageID(),
        message: strings.UNEXPECTED_ERROR,
        dialog: false,
        type: "error",
      });
    } finally {
      setIsRestoringTaskLoading(false);
      setTaskToBeRestored(null);
    }
  }

  function handleRestoreTask(taskDoc: ExistingTask) {
    setConfirmRestoreTaskDialogOpen(true);
    setTaskToBeRestored(taskDoc);
  }

  /**
   * Build an xlsx spreadsheet.
   */
  async function saveSpreadsheet() {
    const columns = [
      {
        key: "id",
        header: "ID",
      },
      {
        key: "craftType",
        header: "Craft Type",
      },
      {
        key: "title",
        header: "Title",
      },
      {
        key: "taskStatus",
        header: "Current Status",
      },
      {
        key: "taskType",
        header: "Task Type",
      },
      {
        key: "description",
        header: "Description",
      },
      {
        key: "timestampCreated",
        header: "Timestamp Created",
      },
      {
        key: "timestampScheduled",
        header: "Timestamp Scheduled",
      },
      {
        key: "timestampTaskStarted",
        header: "Timestamp Task Started",
      },
      {
        key: "timestampTaskCompleted",
        header: "Timestamp Task Completed",
      },
      {
        key: "createdBy",
        header: "Created By",
      },
      {
        key: "location",
        header: "Location",
      },
    ] as Column[];

    Object.values(customColumns).forEach((columnDetails) => {
      const customColumn = {
        key: columnDetails.field,
        header: columnDetails.headerName,
      } as Column;
      columns.push(customColumn);
    });

    const wb = new Workbook();
    const sheet = wb.addWorksheet(strings.DELETED_TASKS);

    sheet.columns = columns;

    const formattedData = formatTaskDataForExcelExport(
      deletedTasks,
      userDisplayNamesMap,
      getLocationTitle,
    );

    sheet.addRows(formattedData);

    // Insert the user's report name as the title row (first row)
    const customTitle = `${userDisplayNamesMap[firebaseUser?.uid ?? ""]} [${
      strings.DELETED_TASKS
    }]`;
    sheet.insertRow(1, [customTitle]);

    // updated the font for first row.
    sheet.getRow(1).font = { bold: true };
    sheet.getRow(2).font = { bold: true };

    try {
      // write the content using writeBuffer
      const buf = await wb.xlsx.writeBuffer();

      // download the processed file
      saveAs(new Blob([buf]), `${customTitle}.xlsx`);
    } catch (error) {
      console.error("Something Went Wrong", error);
    } finally {
      // removing worksheet's instance to create new one
      wb.removeWorksheet(strings.DELETED_TASKS);
    }
  }

  const confirmRestoreTaskDialog = taskToBeRestored && (
    <ConfirmRestoreTaskDialog
      isOpen={confirmRestoreTaskDialogOpen}
      onClose={() => setConfirmRestoreTaskDialogOpen(false)}
      handleConfirmRestoreTask={handleConfirmRestoreTask}
      isSubmitting={isRestoringTaskLoading}
      taskTitle={taskToBeRestored.title}
    />
  );

  /* FUNCTIONS */
  function handleDateRangeChange(
    value: RangeValue<CalendarDate | ZonedDateTime | CalendarDateTime>,
  ) {
    logger.debug(
      `Invoked handleDateRangeChange for range: ${value.start.month}-${value.start.day} to ${value.end.month}-${value.end.day}`,
    );
    try {
      const convertedStart = toCalendarDate(value.start);
      const convertedEnd = toCalendarDate(value.end);
      const converted = { start: convertedStart, end: convertedEnd };
      setRange(converted);
      logger.debug(
        `set range to ${converted.start.month}-${converted.start.day} to ${converted.end.month}-${converted.end.day}`,
      );
    } catch (e) {
      // TODO: Handle this error better
      if (e instanceof Error) {
        console.log("Error in handleDateRangeChange", e);
        logger.error("Error in handleDateRangeChange", e);
      }
    }
  }

  const dateRangePicker = (
    <div className="flex w-full flex-wrap items-center justify-end">
      <span className="pr-2 text-right">{"Date Created"}</span>
      <DateRangePicker value={range} onChange={handleDateRangeChange} />
    </div>
  );

  const actionButtons = (
    <div className="flex gap-4">
      <StyledTooltip title={`Export to Excel`}>
        <button
          onClick={saveSpreadsheet}
          className="rounded-md bg-gray-200 p-2"
        >
          <FileDownloadIcon fontSize="medium" className="shrink-0" />
        </button>
      </StyledTooltip>
      <StyledTooltip title={`Add Additional Columns`}>
        <button
          onClick={() => setTaskTableColumnsDialogOpen(true)}
          className="rounded-md bg-gray-200 p-2"
        >
          <ViewColumnIcon fontSize="medium" className="shrink-0" />
        </button>
      </StyledTooltip>
    </div>
  );

  const createNewTaskButton = (
    <BaseButtonPrimary
      type="button"
      onClick={() => stiltNav.createTaskForCustomer({})}
      className="w-full text-primary sm:w-48"
    >
      <AddCircleIcon fontSize="small" className="mr-2" />
      {strings.NEW_TASK}
    </BaseButtonPrimary>
  );

  const taskTableColumnsDialog = (
    <TaskTableColumnsDialog
      isOpen={taskTableColumnsDialogOpen}
      onClose={() => setTaskTableColumnsDialogOpen(false)}
      setTaskTableColumnsSelection={setTaskTableColumnsSelection}
      getLocationTitle={getLocationTitle}
      getCompanyName={getCompanyName}
      handleRestoreTask={handleRestoreTask}
      additionalColumnsSelection={additionalColumnsSelection}
      siteKeyLocationList={siteKeyLocationList}
      taskTableColumnsSelection={taskTableColumnsSelection}
    />
  );

  // RENDER LOADING
  if (
    deletedTaskListIsLoading ||
    companyStoreIsLoading ||
    locationStoreIsLoading ||
    siteKeyDocIsLoading ||
    userDisplayNamesMapIsLoading
  ) {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <LoadingClipboardAnimation />
      </div>
    );
  }

  //RENDER CONTENT
  return (
    <Fragment>
      <TaskListPage
        taskList={deletedTasks}
        getCompanyName={getCompanyName}
        taskTableColumnsSelection={taskTableColumnsSelection}
        getLocationTitle={getLocationTitle}
        userDisplayNamesMap={userDisplayNamesMap}
        handleRestoreTask={handleRestoreTask}
        customColumns={customColumns}
        siteKeyLocationList={siteKeyLocationList}
      >
        {{
          CreateNewTaskButton: createNewTaskButton,
          ActionButtons: actionButtons,
          DateRangePicker: dateRangePicker,
        }}
      </TaskListPage>
      {confirmRestoreTaskDialog}
      {taskTableColumnsDialog}
    </Fragment>
  );
}
